import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import styles from './VideoStyles.module.css';

import { ProgressBar, Spinner } from '../../components/Progress';
import MainLayout from '../../layouts/MainLayout';

import apiClient from '../../api/apiClient';
import {
  handleDownloadTranscription,
  handleDownloadEmotions,
  handleDownloadSentimentCoding,
} from './DownloadAnalysisPage';
import checkStatus from '../../hooks/checkAnalysisStatus';
import runProgressStep from '../../hooks/runProgressStep';
import SearchableStartupDropdown from '../../components/SearchableStartupDropdown';

const client = apiClient('transcription');
const client_fer = apiClient('emotions');
const client_sentiment = apiClient('sentiment');

const UploadVideo = () => {
  const [startupName, setStartupName] = useState('');
  const [files, setFiles] = useState({
    respondent_baseline1: null,
    respondent_EOD: null,
    respondent_baseline2: null,
    respondent_discussion: null,
    respondent_baseline3: null,
    environment_baseline1: null,
    environment_EOD: null,
    environment_baseline2: null,
    environment_discussion: null,
    environment_baseline3: null,
  });

  const [runTranscription, setRunTranscription] = useState(true);
  const [runEmotionAnalysis, setRunEmotionAnalysis] = useState(true);
  const [runSentimentAnalysis, setRunSentimentAnalysis] = useState(true);
  const [runLLMAnalysis, setRunLLMAnalysis] = useState(true);

  const respondentKeys = Object.keys(files).filter((key) =>
    key.startsWith('respondent')
  );
  const environmentKeys = Object.keys(files).filter((key) =>
    key.startsWith('environment')
  );

  const [uploading, setUploading] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState('');
  const [errors, setError] = useState('');

  const [ferRunning, setFerRunning] = useState(false);
  const [ferProgress, setFerProgress] = useState(0);

  const [sentimentCodingRunning, setSentimentCodingRunning] = useState(false);
  const [sentimentCodingProgress, setsentimentCodingProgress] = useState(0);

  const [llmAnalysisRunning, setLLMAnalysisRunning] = useState(false);
  const [llmAnalysisProgress, setLLMAnalysisProgress] = useState(0);

  const [discussionDuration, setDiscussionDuration] = useState(null); // in seconds

  const handleFileChange = (key, file) => {
    setFiles((prev) => ({ ...prev, [key]: file }));
    // Only get duration for respondent_discussion
    if (key === 'respondent_discussion') {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const duration = video.duration; // in seconds
        console.log(`Discussion video duration: ${duration} seconds`);
        setDiscussionDuration(duration);
      };

      video.src = URL.createObjectURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!startupName) {
      setError('Please select a startup.');
      return;
    }

    if (!files.respondent_discussion) {
      setError(
        'Please provide at least the discussion video of the respondent.'
      );
      return;
    }

    setUploading(true);
    setResult('');

    // send request in order to refresh token before upload
    await client.get(`/status/${startupName}`, {
      validateStatus: () => true,
    });

    const formData = new FormData();
    formData.append('startup_name', startupName);

    for (const [key, file] of Object.entries(files)) {
      if (!file) {
        setError(`Please provide file for: ${key}`);
        setUploading(false);
        return;
      }
      formData.append(key, file);
    }

    // Add flags if transcription is requested
    formData.append('run_transcription', runTranscription);
    formData.append('run_emotion', runEmotionAnalysis);
    formData.append('run_sentiment', runSentimentAnalysis);
    formData.append('run_llm_analysis', runLLMAnalysis);

    try {
      await client.post('/upload/videos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      await new Promise((resolve) => setTimeout(resolve, 2000));

      setUploading(false);

      var statusResp;

      if (runTranscription) {
        statusResp = await client.get(`/status/${startupName}`, {
          validateStatus: () => true,
        });
      } else {
        statusResp = await client_fer.get(`/status/${startupName}`, {
          validateStatus: () => true,
        });
      }

      if (
        statusResp.status === 202 &&
        statusResp.data.status?.includes('in_progress')
      ) {
        if (runTranscription) {
          startTranscriptionStep();
        } else {
          startFERStep();
        }
      } else {
        setError(
          'Unexpected status: ' +
            (statusResp.data.status || 'No status') +
            ' with error: ' +
            (statusResp.data.detail || 'No error')
        );
      }
    } catch (error) {
      setUploading(false);
      setError('Upload error: ' + error.message);
    }
  };

  const startTranscriptionStep = () => {
    runProgressStep({
      setRunning: setTranscribing,
      setProgress: setProgress,
      fallbackDurationMs: 15 * 60 * 1000,
      estimatedDurationSeconds: discussionDuration,
      onComplete: checkTranscriptionStatus,
    });
  };

  const startSentimentCodingStep = () => {
    runProgressStep({
      setRunning: setSentimentCodingRunning,
      setProgress: setsentimentCodingProgress,
      fallbackDurationMs: 30 * 1000, // 30s fallback
      estimatedDurationSeconds: 30, // max 30s for sentiment coding
      onComplete: checkSentimentCodingStatus,
      scale: 1.0,
    });
  };

  const startLLMAnalysisStep = () => {
    runProgressStep({
      setRunning: setLLMAnalysisRunning,
      setProgress: setLLMAnalysisProgress,
      fallbackDurationMs: 15 * 60 * 1000,
      estimatedDurationSeconds: discussionDuration,
      onComplete: checkLLMAnalysisStatus,
      scale: 0.6,
    });
  };

  const startFERStep = () => {
    runProgressStep({
      setRunning: setFerRunning,
      setProgress: setFerProgress,
      fallbackDurationMs: 15 * 60 * 1000,
      estimatedDurationSeconds: discussionDuration,
      onComplete: checkEmotionsStatus,
      scale: 0.6,
    });
  };

  const checkTranscriptionStatus = () => {
    checkStatus({
      client,
      endpoint: `/status/${startupName}`,
      setRunning: setTranscribing,
      setError,
      onSuccess: () => setResult('Analysis completed successfully!'),
      nextStep: runSentimentAnalysis
        ? startSentimentCodingStep
        : runLLMAnalysis
          ? startLLMAnalysisStep
          : runEmotionAnalysis
            ? startFERStep
            : null,
      pollingLabel: 'Transcription',
    });
  };

  const checkSentimentCodingStatus = () => {
    checkStatus({
      client: client_sentiment,
      endpoint: `/status/coding/${startupName}`,
      setRunning: setSentimentCodingRunning,
      setError,
      onSuccess: () => setResult('Analysis completed successfully!'),
      nextStep: runLLMAnalysis
        ? startLLMAnalysisStep
        : runEmotionAnalysis
          ? startFERStep
          : null,
      pollingLabel: 'Sentiment Coding',
    });
  };

  const checkLLMAnalysisStatus = () => {
    checkStatus({
      client: client_sentiment,
      endpoint: `/status/llm/${startupName}`,
      setRunning: setLLMAnalysisRunning,
      setError,
      onSuccess: () => setResult('Analysis completed successfully!'),
      nextStep: runEmotionAnalysis ? startFERStep : null,
      pollingLabel: 'LLM Analysis',
    });
  };

  const checkEmotionsStatus = () => {
    checkStatus({
      client: client_fer,
      endpoint: `/status/${startupName}`,
      setRunning: setFerRunning,
      setError,
      onSuccess: () => setResult('Analysis should now be completed!'),
      pollingLabel: 'Emotions Analysis',
    });
  };

  return (
    <MainLayout>
      <div style={{ margin: '20px' }}>
        <h1>Upload Interview Video Files</h1>
        <div className={styles.filterBox}>
          <div className={styles.filterRow}>
            <label className={styles.filterLabel}>Startup Name:</label>
            <div style={{ flex: 1 }}>
              <SearchableStartupDropdown
                selectedStartup={startupName}
                setSelectedStartup={setStartupName}
              />
            </div>
          </div>
          <div className={styles.checkboxRow}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={runTranscription}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setRunTranscription(checked);
                  if (!checked) {
                    setRunSentimentAnalysis(false);
                    setRunLLMAnalysis(false);
                  }
                }}
                disabled={uploading || transcribing}
              />
              Transcription
            </label>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={runSentimentAnalysis}
                onChange={(e) => setRunSentimentAnalysis(e.target.checked)}
                disabled={!runTranscription || uploading || transcribing}
              />
              Sentiment Analysis
            </label>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={runLLMAnalysis}
                onChange={(e) => setRunLLMAnalysis(e.target.checked)}
                disabled={!runTranscription || uploading || transcribing}
              />
              LLM Analysis
            </label>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={runEmotionAnalysis}
                onChange={(e) => setRunEmotionAnalysis(e.target.checked)}
                disabled={uploading || transcribing}
              />
              Emotion Analysis
            </label>
          </div>
          {!runTranscription && (
            <Alert severity="info">
              Sentiment and LLM analysis requires transcription. Enable
              transcription to use this step.
            </Alert>
          )}
        </div>
        {/* Upload-Formular nur für Dateiuploads */}
        <form onSubmit={handleSubmit} className={styles.uploadFormResponsive}>
          <div className={styles.uploadSections}>
            <div className={styles.uploadBox}>
              <h2>Respondent Videos</h2>
              {respondentKeys.map((key) => (
                <div className={styles.uploadRow} key={key}>
                  <label>{key.replace(/_/g, ' ')}:</label>
                  <input
                    type="file"
                    accept="video/*"
                    required
                    onChange={(e) => handleFileChange(key, e.target.files[0])}
                    disabled={uploading || transcribing}
                  />
                </div>
              ))}
            </div>
            <div className={styles.uploadBox}>
              <h2>Environment Videos</h2>
              {environmentKeys.map((key) => (
                <div className={styles.uploadRow} key={key}>
                  <label>{key.replace(/_/g, ' ')}:</label>
                  <input
                    type="file"
                    accept="video/*"
                    required
                    onChange={(e) => handleFileChange(key, e.target.files[0])}
                    disabled={uploading || transcribing}
                  />
                </div>
              ))}
            </div>
          </div>
          <button type="submit" disabled={uploading || transcribing}>
            Upload All Videos
          </button>
        </form>
        {uploading && (
          <p>
            Uploading... <Spinner />
          </p>
        )}
        {transcribing && (
          <div>
            <p>
              Transcription in progress...
              {discussionDuration
                ? ` approx. ${Math.ceil(discussionDuration / 60)} min`
                : ' approx. 15 min'}
            </p>
            <ProgressBar percentage={progress} />
          </div>
        )}
        {sentimentCodingRunning && (
          <div>
            <p>✅ Transcription</p>
            <p>Sentiment Coding in progress... approx. 30 seconds</p>
            <ProgressBar percentage={sentimentCodingProgress} />
          </div>
        )}
        {llmAnalysisRunning && (
          <div>
            <p>✅ Transcription</p>
            {runSentimentAnalysis && <p>✅ Sentiment Coding</p>}
            <p>
              LLM Analysis in progress...
              {discussionDuration
                ? ` approx. ${Math.ceil((discussionDuration / 60) * 0.6)} min`
                : ' approx. 10 min'}
            </p>
            <ProgressBar percentage={llmAnalysisProgress} />
          </div>
        )}
        {ferRunning && (
          <div>
            {runTranscription && <p>✅ Transcription</p>}
            {runSentimentAnalysis && <p>✅ Sentiment Coding</p>}
            {runLLMAnalysis && <p>✅ LLM Analysis</p>}
            <p>
              Facial Emotion Recognition in progress...
              {discussionDuration
                ? ` approx. ${Math.ceil((discussionDuration / 60) * 0.6)} min`
                : ' approx. 10 min'}
            </p>
            <ProgressBar percentage={ferProgress} />
          </div>
        )}
        {errors && (
          <>
            <h2>Could not transcribe or analyse emotions:</h2>
            <p>{errors}</p>
          </>
        )}
        {result &&
          !ferRunning &&
          !transcribing &&
          !sentimentCodingRunning &&
          !llmAnalysisRunning && (
            <div>
              <h2>Analysis Complete:</h2>
              <p>{result}</p>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {runTranscription && (
                  <button
                    onClick={() => handleDownloadTranscription(startupName)}
                  >
                    Download Transcription
                  </button>
                )}
                {runEmotionAnalysis && (
                  <button onClick={() => handleDownloadEmotions(startupName)}>
                    Download Emotions Analysis
                  </button>
                )}
                {runSentimentAnalysis && (
                  <button
                    onClick={() => handleDownloadSentimentCoding(startupName)}
                  >
                    Download Sentiment Analysis
                  </button>
                )}
              </div>
            </div>
          )}
      </div>
    </MainLayout>
  );
};

export default UploadVideo;
